import { clampLedgerText, plainLedgerText } from './goal-ledger';
import { dayLabel, type DayLabel } from './outreach-thread-view';

/**
 * What a question card READS, as opposed to what it lets you press (`taskDecisionShape`).
 *
 * A long question used to drop the row to a single "Answer this" chip, so an approval whose question ran
 * long showed no Approve / Decline at all (reported on the phone's "Waiting on you" view). The answer chips
 * now stay whatever the length, and the LENGTH is handled here instead: the question and the agent's
 * `detail` are clamped on a word boundary with an ellipsis, and one "More about this" toggle shows both
 * whole. Nothing is cut mid-word and nothing is cut without a way to read it.
 *
 * Shared 1:1 with iOS `TaskDecisionPrompts.body` and Android `promptBody`, which use their own ledger clamp
 * (`clampLedgerText`) at the same limit.
 */

/** How much of the question and of the detail a collapsed card shows. The old inline-question limit. */
export const DECISION_BODY_LIMIT = 140;
/** How many lines of a LIST detail (two or more lines) a collapsed card shows. */
export const DECISION_DETAIL_LINES = 6;
/** How much of each of those lines a collapsed card shows. */
export const DECISION_DETAIL_LINE_LIMIT = 100;

export interface DecisionPromptBody {
  /** The question as the card shows it; undefined when blank (the caller shows its "Waiting on you" copy). */
  question?: string;
  /**
   * The agent's context, markdown stripped; undefined when there is none. A list detail (a marketing plan's
   * dated steps) keeps its lines, joined with `\n`, so the renderer must show line breaks (`white-space: pre-line`).
   */
  detail?: string;
  /** Whether either was clamped — the only case the toggle is drawn. */
  expandable: boolean;
}

export function decisionPromptBody(question: string | undefined, detail: string | undefined, expanded: boolean): DecisionPromptBody {
  const fullQuestion = question?.trim().replace(/\s+/g, ' ') || undefined;
  const q = fullQuestion ? clampLedgerText(fullQuestion, DECISION_BODY_LIMIT) : undefined;
  const d = detailBody(detail);
  return {
    ...(fullQuestion ? { question: expanded ? fullQuestion : q!.text } : {}),
    ...(d ? { detail: expanded ? d.full : d.collapsed } : {}),
    expandable: !!q?.truncated || !!d?.truncated,
  };
}

/**
 * The goal to open when a question is a PLAN to review, else undefined. A plan approval carries `planGoalId`
 * (common-lib stamps it from the decision's `display.scope` = `plan`), and a card that shows one draws the question
 * and "See the full plan", which opens the goal: its timeline, its numbered steps and the same question with
 * Approve / Decline under them. Approving a plan from a squeezed list of its step titles was not an informed answer.
 *
 * Keyed on that marker, never on the detail's shape or the task's goal: a step's OWN question is not on the goal's
 * ledger, so sending it there would leave it unanswerable. Read loosely, so a common-lib build without the field fits.
 * Shared 1:1 with iOS `TaskDecisionPrompts.planReviewGoalId` and Android `planReviewGoalId`.
 */
export function planReviewGoalId(prompt: object): string | undefined {
  const value = (prompt as { planGoalId?: unknown }).planGoalId;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * The detail, collapsed and whole. A plan approval's detail is a LIST — one line per dated step, then a note —
 * and collapsing its newlines into one 140-character run showed a step and a half of the plan the owner was
 * asked to approve. So each line is stripped and collapsed on its own, empty lines dropped, and:
 * - one line (a paragraph) clamps at `DECISION_BODY_LIMIT`, as before;
 * - two or more SHORT lines (a list: none over `DECISION_BODY_LIMIT`) show the first `DECISION_DETAIL_LINES`, each
 *   clamped at `DECISION_DETAIL_LINE_LIMIT`; longer lines are prose and read as one paragraph, as before.
 */
function detailBody(detail: string | undefined): { full: string; collapsed: string; truncated: boolean } | undefined {
  const lines = (detail ?? '')
    .split('\n')
    .map((line) => plainLedgerText(line))
    .filter((line) => line.length > 0);
  if (lines.length === 0) return undefined;
  // Only SHORT lines make a list. Paragraphs of prose split by blank lines stay one paragraph, as before.
  if (lines.length === 1 || lines.some((line) => line.length > DECISION_BODY_LIMIT)) {
    const paragraph = lines.join(' ');
    const clamped = clampLedgerText(paragraph, DECISION_BODY_LIMIT);
    return { full: paragraph, collapsed: clamped.text, truncated: clamped.truncated };
  }
  const shown = lines.slice(0, DECISION_DETAIL_LINES).map((line) => clampLedgerText(line, DECISION_DETAIL_LINE_LIMIT));
  return {
    full: lines.join('\n'),
    collapsed: shown.map((line) => line.text).join('\n'),
    truncated: lines.length > DECISION_DETAIL_LINES || shown.some((line) => line.truncated),
  };
}

/**
 * The day an ask was RAISED, as a card shows it: Today / Yesterday / "Oct 23" (+ the year when not this year).
 * A question that says "tomorrow" is only readable against the day it was asked. The first instant present wins
 * (the decision's own `createdDate`, then the ask's). Rendered with `renderDayLabel`.
 */
export function raisedDay(candidates: (string | undefined | null)[], now: Date, timeZone?: string): DayLabel | null {
  for (const at of candidates) {
    const label = dayLabel(at ?? null, now, timeZone);
    if (label) return label;
  }
  return null;
}
